// Sim: owns MatchState + NavGrid, drains SimCommands, runs the fixed tick pipeline (§8.3).

import { mapDef } from '../content/map';
import { orderDeckByAscension } from '../content/deckOrder';
import { factionDef, type MatchSetup, type PlayerSetup } from '../content/factions';
import { buildingDef, cardDef, defs, unitDef } from '../content/registry';
import { TUNING } from '../content/tuning';
import type { AnnexEffect, MapDef, PlayerId, TeamId, UnitDef, UpgradeDef } from '../content/types';
import { SimRng } from '../shared/rng';
import type { Vec2 } from '../shared/vec';
import { dist, vec } from '../shared/vec';
import type { CommandResult, SimCommand } from './commands';
import type {
  Building,
  Deposit,
  EntId,
  MatchState,
  Mine,
  PlayerState,
  Projectile,
  SimEvent,
  Squad,
  WorkerEnt,
} from './state';
import { armorReductionOf, buffMult, footprintOf, makeAnnexState } from './state';
import { NavGrid } from './nav';
import { buildingAccessPoint, targetPosition } from './targeting';
import { stepAbilities } from './systems/abilities';
import { stepAnnexes } from './systems/annexes';
import { stepBuildingTraits } from './systems/buildingTraits';
import { canPlayBase, stepCards, processPlayCard } from './systems/cards';
import { stepCombat } from './systems/combat';
import { stepCommands } from './systems/commandsHl';
import { stepConstruction } from './systems/construction';
import { planFormation } from './systems/formation';
import { stepCoreDefense } from './systems/coreDefense';
import { stepCorpse } from './systems/corpse';
import { gateApertureOccupied, stepGates, wallGateRect } from './systems/gates';
import {
  dismountWallSquad,
  stepMounting,
  wallMountCapacity,
  wallMountSlotPosition,
} from './systems/mounting';
import { stepMovement } from './systems/movement';
import { stepNetwork } from './systems/network';
import { stepProduction } from './systems/production';
import { stepRecovery } from './systems/recovery';
import { stepRituals } from './systems/rituals';
import { stepSites } from './systems/sites';
import { stepSpellsAndZones } from './systems/spells';
import { stepTraps } from './systems/traps';
import { stepWorkers } from './systems/workers';
import { areAllies, areHostile, enemyPlayerIds, opposingTeam, playerIds, teamOf } from './teams';

function makePlayer(id: PlayerId, setup: PlayerSetup): PlayerState {
  const orderedDeck = orderDeckByAscension(setup.deck);
  const cards: PlayerState['cards'] = {};
  for (const cid of orderedDeck) {
    const def = cardDef(cid);
    cards[cid] = {
      cooldownRemaining: 0,
      usesRemaining:
        def.useModel.kind === 'limited'
          ? def.useModel.maxUses
          : def.useModel.kind === 'once'
            ? 1
            : Infinity,
      activeCopies: 0,
    };
  }
  const fac = factionDef(setup.faction);
  return {
    id,
    team: setup.team,
    faction: setup.faction,
    isAI: setup.isAI,
    power: TUNING.startPower,
    essence: TUNING.startEssence,
    allocationPower: TUNING.allocationDefaultPower,
    autoWorker: false,
    workforceUsed: 0,
    workforceCap: fac.workersPerZone,
    commandUsed: 0,
    commandCap: TUNING.commandCore,
    ascensionDiscovered: 1,
    ascensionPlayable: 1,
    cards,
    deck: orderedDeck,
    shelterOccupants: [],
    shelterCapacity: fac.shelterCapacity,
    shelterReleaseTimer: 0,
    workerQueue: [],
    noEssenceWarning: false,
    coreDefenseCooldowns: {},
    corpseStored: 0,
    awakeningRemaining: 0,
    devouredMass: 0,
    convergenceRemaining: 0,
    annex: makeAnnexState(),
    stats: {
      unitsBuilt: 0,
      unitsLost: 0,
      sitesCaptured: 0,
      coreDamageDealt: 0,
      powerGathered: 0,
      essenceGathered: 0,
      cardsPlayed: 0,
    },
  };
}

export class Sim {
  state: MatchState;
  nav: NavGrid;
  map: MapDef;
  private queue: { command: SimCommand; onResult?: (result: CommandResult) => void }[] = [];
  private epicBattleStaged = false;

  constructor(seed: number, setup: MatchSetup) {
    this.map = mapDef(setup.mapId);
    if (this.map.mode !== setup.mode || this.map.corePositions.length !== setup.players.length) throw new Error(`Map ${setup.mapId} is incompatible with ${setup.mode}`);
    if (setup.players.some((player, slot) => player.team !== this.map.spawnTeams[slot])) throw new Error(`Map ${setup.mapId} has an invalid spawn-team assignment`);
    this.nav = new NavGrid(this.map, setup.players.map((player) => player.team));
    this.state = {
      tick: 0,
      time: 0,
      phase: 'countdown',
      countdownRemaining: TUNING.countdownSeconds,
      winningTeam: -1,
      seed,
      rng: new SimRng(seed),
      nextId: 1,
      players: setup.players.map((player, index) => makePlayer(index as PlayerId, player)),
      squads: new Map(),
      workers: new Map(),
      buildings: new Map(),
      deposits: new Map(),
      projectiles: new Map(),
      mines: new Map(),
      zones: new Map(),
      ruins: [],
      corpseMarks: [],
      rituals: [],
      pendingSummons: [],
      connectedConduits: new Set(),
      recentDeaths: [],
      sites: this.map.sites.map((s) => ({
        id: s.id,
        name: s.name,
        pos: s.pos,
        owner: -1 as const,
        meter: 0,
        meterPlayer: -1 as const,
        control: 0,
        contested: false,
        decayGrace: 0,
      })),
      events: [],
      nextFieldId: 1,
      paused: false,
    };
    this.setupMatch();
  }

  // ---------------------------------------------------------------- setup

  private setupMatch(): void {
    // ruins / wall foundations
    for (const spec of this.map.ruins) {
      this.state.ruins.push({
        id: this.newId(),
        pos: { ...spec.pos },
        buildingId: null,
        cooldownUntil: 0,
        cost: spec.cost ?? TUNING.wallClaimCost,
        gateSpan: spec.gateSpan ?? 8, // giant-safe shared auto-gate by default
        rot: spec.rot ?? 0,
        group: spec.group ?? null,
      });
    }
    // deposits
    for (const d of this.map.deposits) {
      const id = this.newId();
      this.state.deposits.set(id, {
        id,
        type: d.type,
        pos: d.pos,
        amount: d.amount,
        initialAmount: d.amount,
        gatherSlots: d.gatherSlots,
      });
    }
    // cores + starting workers (startWorkers is 0 — the player trains the first ones)
    for (const p of this.playerIds()) {
      const corePos = this.map.corePositions[p];
      const fac = factionDef(this.state.players[p].faction);
      const core = this.spawnBuilding(fac.coreDefId, p, corePos, { complete: true, isCore: true });
      for (let i = 0; i < TUNING.startWorkers; i++) {
        const angle = (i / TUNING.startWorkers) * Math.PI * 2;
        this.spawnWorker(p, vec(corePos.x + Math.cos(angle) * 6, corePos.y + Math.sin(angle) * 6), undefined, core.id);
      }
      this.recomputeCaps(p);
    }
  }

  playerIds(): PlayerId[] {
    return playerIds(this.state);
  }

  teamOf(player: PlayerId): TeamId {
    return teamOf(this.state, player);
  }

  areAllies(a: PlayerId, b: PlayerId): boolean {
    return areAllies(this.state, a, b);
  }

  areHostile(a: PlayerId, b: PlayerId): boolean {
    return areHostile(this.state, a, b);
  }

  enemiesOf(player: PlayerId): PlayerId[] {
    return enemyPlayerIds(this.state, player);
  }

  teamHasCore(team: TeamId): boolean {
    return this.state.players.some((player) => player.team === team && this.coreAlive(player.id));
  }

  /** Server-side surrender/timeout cleanup. Combat Core loss intentionally leaves survivors. */
  eliminatePlayer(player: PlayerId, deferVictory = false): void {
    for (const squad of [...this.state.squads.values()]) if (squad.owner === player) this.despawnSquad(squad);
    for (const worker of [...this.state.workers.values()]) if (worker.owner === player) this.state.workers.delete(worker.id);
    const buildings = [...this.state.buildings.values()].filter((building) => building.owner === player);
    const core = buildings.find((building) => building.isCore);
    for (const building of buildings) if (!building.isCore) this.destroyBuildingSilently(building);
    for (const site of this.state.sites) {
      if (site.owner !== player) continue;
      site.owner = -1;
      site.control = 0;
      site.meter = 0;
      site.meterPlayer = -1;
      this.pushEvent({ type: 'SiteLost', site: site.id, loser: player });
      this.pushEvent({ type: 'SiteNeutralized', site: site.id });
    }
    if (core) this.destroyBuilding(core);
    if (!deferVictory && this.state.phase !== 'ended' && !this.teamHasCore(this.teamOf(player))) {
      this.state.phase = 'ended';
      this.state.winningTeam = opposingTeam(this.teamOf(player));
      this.pushEvent({ type: 'MatchEnded', winningTeam: this.state.winningTeam });
    }
    this.recomputeCaps(player);
  }

  // ---------------------------------------------------------------- ids / events

  newId(): EntId {
    return this.state.nextId++;
  }

  pushEvent(e: SimEvent): void {
    this.state.events.push(e);
  }

  // ---------------------------------------------------------------- spawns

  spawnSquad(
    defId: string,
    owner: PlayerId,
    pos: Vec2,
    opts?: {
      maxHealthMult?: number;
      temporarySeconds?: number;
      /** Core Annex System: annex upgrade id that grew this unit */
      sourceAnnexId?: string;
      /** does not count against Command (Astral Echoes) */
      commandFree?: boolean;
      isEcho?: boolean;
      echoLink?: { conduitId: EntId; remaining: number };
    },
  ): Squad {
    const d = unitDef(defId);
    const id = this.newId();
    const maxHealth = Math.round(d.maxHealth * (opts?.maxHealthMult ?? 1));
    const p = this.nav.nearestWalkable(pos);
    const squad: Squad = {
      id,
      defId,
      owner,
      pos: { ...p },
      prevPos: { ...p },
      lastOpenPos: { ...p },
      facing: 0,
      health: maxHealth,
      maxHealth,
      tempHealth: 0,
      tempHealthRemaining: 0,
      buffs: [],
      marks: [],
      command: { kind: 'idle', pointA: { ...p } },
      breachResume: null,
      path: null,
      pathIndex: 0,
      pathGoal: null,
      pathNavRevision: this.nav.revision,
      repathCooldown: 0,
      stuckTime: 0,
      progressGoal: null,
      bestRouteRemaining: Infinity,
      progressNavRevision: this.nav.revision,
      progressRouteSignature: 0,
      moveTarget: null,
      commandIssuedTick: -1,
      attackCooldown: 0,
      targetId: null,
      retargetTimer: 0,
      targetSince: -999,
      lastDamagedAt: -999,
      lastCombatAt: -999,
      abilityCooldowns: {},
      abilityFiredOnce: {},
      telegraph: null,
      escortOf: null,
      escortSlot: null,
      mountedOnWall: null,
      mountedOnSquad: null,
      rootedRemaining: 0,
      groupSpeedCap: null,
      windupRemaining: 0,
      windupTargetId: null,
      knockdownRemaining: 0,
      knockdownTotal: 0,
      temporaryRemaining: opts?.temporarySeconds ?? null,
      sourceAnnexId: opts?.sourceAnnexId ?? null,
      commandFree: !!opts?.commandFree,
      isEcho: !!opts?.isEcho,
      echoLink: opts?.echoLink ?? null,
      treeRooted: false,
      uprootRemaining: 0,
      auraSuppressedRemaining: 0,
      healingTakenMult: 1,
      healingTakenRemaining: 0,
      structureAttackCount: 0,
      engaged: false,
      charge: null,
      isBoss: !!d.isBoss,
      dead: false,
    };
    this.state.squads.set(id, squad);
    this.state.players[owner].stats.unitsBuilt++;
    this.pushEvent({ type: 'SquadSpawned', id, owner });
    this.recomputeCaps(owner);
    return squad;
  }

  /** Apply the shared A-22 defend-at-rally behavior to a military spawn source. */
  sendSpawnToRally(squad: Squad, source: Building): void {
    squad.command = { kind: 'defend', pointA: { ...source.rally }, auto: true };
  }

  spawnWorker(owner: PlayerId, pos: Vec2, defId?: string, homeId?: EntId): WorkerEnt {
    const id = this.newId();
    const p = this.nav.nearestWalkable(pos);
    const wDefId = defId ?? factionDef(this.state.players[owner].faction).workerUnitId;
    const health = unitDef(wDefId).maxHealth;
    const w: WorkerEnt = {
      id,
      defId: wDefId,
      homeId: homeId ?? this.nearestWorkerHub(owner, p)?.id ?? null,
      owner,
      pos: { ...p },
      prevPos: { ...p },
      lastOpenPos: { ...p },
      health,
      maxHealth: health,
      state: 'idle',
      carrying: null,
      depositId: null,
      buildTargetId: null,
      repairTargetId: null,
      path: null,
      pathIndex: 0,
      stuckTime: 0,
      recoveries: 0,
      gatherTimer: 0,
      fleeTimer: 0,
      lastDamagedAt: -999,
      dead: false,
      overCap: false,
      arrivalRemaining: 0,
      recallChannelRemaining: 0,
    };
    this.state.workers.set(id, w);
    this.recomputeCaps(owner);
    return w;
  }

  spawnBuilding(
    defId: string,
    owner: PlayerId,
    pos: Vec2,
    opts?: {
      complete?: boolean;
      isCore?: boolean;
      siteId?: string | null;
      cardId?: string | null;
      expiresSeconds?: number;
      gateSpan?: number;
      rot?: 0 | 1;
      /** Core Annex System: annex upgrade id this structure came from */
      annexSourceId?: string;
    },
  ): Building {
    const d = buildingDef(defId);
    const id = this.newId();
    const complete = !!opts?.complete;
    const b: Building = {
      id,
      defId,
      owner,
      pos: { ...pos },
      modeActive: false,
      state: complete ? 'active' : 'blueprint',
      health: complete ? d.maxHealth : Math.round(d.maxHealth * 0.1),
      maxHealth: d.maxHealth,
      tempHealth: 0,
      tempHealthRemaining: 0,
      buildProgress: complete ? 1 : 0,
      queue: [],
      autoProduce: {},
      rally: vec(pos.x, pos.y - d.footprint.h / 2 - 2),
      upgradeDone: false,
      upgradeRemaining: 0,
      upgradesDone: [],
      upgradeInProgress: null,
      attackCooldown: 0,
      targetId: null,
      lastDamagedAt: -999,
      lastSiegeDamagedAt: -999,
      healOverTime: [],
      siteId: opts?.siteId ?? null,
      isCore: !!opts?.isCore,
      lastCourseFired: false,
      cardId: opts?.cardId ?? null,
      repairResist: 0,
      repairHalvedRemaining: 0,
      gateMarksRemaining: d.gate?.marks ?? 0,
      expiresRemaining: opts?.expiresSeconds ?? null,
      fireSuppressedRemaining: 0,
      counter: 0,
      // owner feedback: towers must NOT auto-train unless the player opts in via the tower panel
      workerAuto: false,
      workerQueueRemaining: null,
      recallActive: false,
      navStamped: false,
      gateSpan: opts?.gateSpan ?? 0,
      gateOpen: false,
      gateNavOpen: false,
      gateOpenFraction: 0,
      gateHoldRemaining: 0,
      rot: opts?.rot ?? 0,
      annexSourceId: opts?.annexSourceId ?? null,
      witherRemaining: null,
      spawnCooldown: 0,
      traitCooldowns: {},
      traitCounters: {},
      lastTraitDeathAt: -1,
      captureTrainingRemaining: 0,
    };
    this.state.buildings.set(id, b);
    if (!complete && (d.kind === 'wall' || (d.kind === 'defense' && !!d.attack && !d.isSiteTower))) {
      const fabricator = [...this.state.buildings.values()]
        .filter((other) => other.owner === owner && other.id !== id && other.state === 'active')
        .sort((a, c) => a.id - c.id)
        .find((other) => {
          const trait = buildingDef(other.defId).traits?.find((x) => x.kind === 'prefabrication');
          return !!trait && (!trait.upgradeOnly || other.upgradeDone) && (other.traitCooldowns.prefabrication ?? 0) <= 0;
        });
      if (fabricator) {
        const trait = buildingDef(fabricator.defId).traits!.find((x) => x.kind === 'prefabrication')!;
        if (trait.kind === 'prefabrication') {
          b.buildProgress = trait.startingProgress;
          b.health = Math.max(b.health, Math.round(b.maxHealth * trait.startingProgress));
          fabricator.traitCooldowns.prefabrication = trait.cooldown;
        }
      }
    }
    // stamp footprint — walls stay passable until construction completes (owner request);
    // construction.ts stamps them on activation
    if (complete || d.kind !== 'wall') this.stampBuildingNav(b, 1);
    return b;
  }

  /** Stamp or unstamp a footprint, including a shared physical castle-gate aperture. */
  stampBuildingNav(b: Building, delta: 1 | -1): void {
    if (delta === 1 && b.navStamped) return;
    if (delta === -1 && !b.navStamped) return;
    b.navStamped = delta === 1;
    const d = buildingDef(b.defId);
    const fp = footprintOf(d, b.rot);
    const x0 = b.pos.x - fp.w / 2;
    const y0 = b.pos.y - fp.h / 2;
    const gate = wallGateRect(b);
    if (!gate) {
      this.nav.stampRect(x0, y0, fp.w, fp.h, delta);
      if (delta === 1 && d.kind === 'wall') this.evacuateStampedWall(b, fp);
      return;
    }
    if (fp.h >= fp.w) {
      this.nav.stampRect(x0, y0, fp.w, gate.y0 - y0, delta);
      this.nav.stampRect(x0, gate.y1, fp.w, y0 + fp.h - gate.y1, delta);
    } else {
      this.nav.stampRect(x0, y0, gate.x0 - x0, fp.h, delta);
      this.nav.stampRect(gate.x1, y0, x0 + fp.w - gate.x1, fp.h, delta);
    }

    if (delta === 1) {
      // A wall may finish while armies are crossing its blueprint. Start completely open
      // rather than stamping a door over any formation already occupying the aperture.
      if (gateApertureOccupied(this, b)) {
        b.gateOpen = true;
        b.gateNavOpen = true;
        b.gateOpenFraction = 1;
        b.gateHoldRemaining = TUNING.wallGateHoldSeconds;
        this.pushEvent({ type: 'GateOpened', id: b.id, owner: b.owner, pos: { ...b.pos } });
      } else {
        this.nav.stampGate(gate.x0, gate.y0, gate.x1 - gate.x0, gate.y1 - gate.y0, b.owner, 1);
      }
      this.evacuateStampedWall(b, fp);
    } else if (!b.gateNavOpen) {
      this.nav.stampGate(gate.x0, gate.y0, gate.x1 - gate.x0, gate.y1 - gate.y0, b.owner, -1);
    }
  }

  /** Toggle shared gate collision after the door animation reaches its safe threshold. */
  setWallGateNavOpen(wall: Building, open: boolean): void {
    if (!wall.navStamped || wall.gateNavOpen === open) return;
    const gate = wallGateRect(wall);
    if (!gate) return;
    this.nav.stampGate(
      gate.x0,
      gate.y0,
      gate.x1 - gate.x0,
      gate.y1 - gate.y0,
      wall.owner,
      open ? -1 : 1,
    );
    wall.gateNavOpen = open;
    this.pushEvent({
      type: open ? 'GateOpened' : 'GateClosed',
      id: wall.id,
      owner: wall.owner,
      pos: { ...wall.pos },
    });
  }

  /** Remove actors caught in the solid part of a wall as its construction completes. */
  private evacuateStampedWall(wall: Building, fp: { w: number; h: number }): void {
    const inside = (pos: Vec2): boolean =>
      Math.abs(pos.x - wall.pos.x) <= fp.w / 2 && Math.abs(pos.y - wall.pos.y) <= fp.h / 2;
    const radius = Math.max(fp.w, fp.h) + 8;
    for (const squad of this.state.squads.values()) {
      if (squad.mountedOnWall !== null || squad.mountedOnSquad !== null || !inside(squad.pos)) continue;
      if (this.nav.isWalkable(squad.pos)) continue;
      const prefer = dist(squad.pos, squad.lastOpenPos) > 0.1
        ? squad.lastOpenPos
        : this.map.corePositions[squad.owner];
      squad.pos = this.nav.nearestWalkable(squad.pos, radius, squad.owner, prefer);
      squad.prevPos = { ...squad.pos };
      squad.lastOpenPos = { ...squad.pos };
      squad.path = null;
      squad.pathGoal = null;
      squad.pathIndex = 0;
      squad.repathCooldown = 0;
      squad.stuckTime = 0;
      squad.progressGoal = null;
      squad.bestRouteRemaining = Infinity;
      squad.progressNavRevision = this.nav.revision;
      squad.progressRouteSignature = 0;
    }
    for (const worker of this.state.workers.values()) {
      if (worker.state === 'sheltered' || !inside(worker.pos)) continue;
      if (this.nav.isWalkable(worker.pos)) continue;
      const prefer = dist(worker.pos, worker.lastOpenPos) > 0.1
        ? worker.lastOpenPos
        : this.map.corePositions[worker.owner];
      worker.pos = this.nav.nearestWalkable(worker.pos, radius, worker.owner, prefer);
      worker.prevPos = { ...worker.pos };
      worker.lastOpenPos = { ...worker.pos };
      worker.path = null;
      worker.pathIndex = 0;
      worker.stuckTime = 0;
    }
  }

  spawnProjectile(
    owner: PlayerId,
    from: Vec2,
    targetId: EntId,
    targetKind: Projectile['targetKind'],
    speed: number,
    damage: number,
    structureMult: number,
    sourceSquadId: EntId | null,
  ): void {
    const id = this.newId();
    this.state.projectiles.set(id, {
      id,
      owner,
      pos: { ...from },
      prevPos: { ...from },
      targetId,
      targetKind,
      speed,
      damage,
      isRanged: true,
      structureMult,
      sourceSquadId,
      dead: false,
    });
  }

  // ---------------------------------------------------------------- queries

  core(owner: PlayerId): Building {
    for (const b of this.state.buildings.values()) {
      if (b.isCore && b.owner === owner && b.state !== 'destroyed') return b;
    }
    throw new Error(`Core missing for player ${owner}`);
  }

  coreAlive(owner: PlayerId): Building | null {
    for (const b of this.state.buildings.values()) {
      if (b.isCore && b.owner === owner && b.state !== 'destroyed') return b;
    }
    return null;
  }

  sitesOwned(owner: PlayerId): number {
    return this.state.sites.filter((s) => s.owner === owner).length;
  }

  // ---------------------------------------------------------------- worker zones

  /** Buildings that host workers: the Core plus every active site tower. */
  workerHubs(owner: PlayerId): Building[] {
    const hubs: Building[] = [];
    for (const b of this.state.buildings.values()) {
      if (b.owner !== owner || b.state !== 'active') continue;
      if (b.isCore || buildingDef(b.defId).producesWorkers) hubs.push(b);
    }
    return hubs;
  }

  nearestWorkerHub(owner: PlayerId, pos: Vec2): Building | null {
    let best: Building | null = null;
    let bestD = Infinity;
    for (const hub of this.workerHubs(owner)) {
      const d = dist(hub.pos, pos);
      if (d < bestD) {
        bestD = d;
        best = hub;
      }
    }
    return best;
  }

  /** Worker cap of one zone. Workforce annexes/buildings extend the Core zone. */
  /** The base-ring ruin group this player owns (west/east of the mirror axis). */
  baseWallGroup(player: PlayerId): string {
    return this.map.corePositions[player].x < this.map.width / 2 ? 'base-w' : 'base-e';
  }

  zoneWorkerCap(owner: PlayerId, hub: Building): number {
    const p = this.state.players[owner];
    let cap = factionDef(p.faction).workersPerZone;
    if (hub.isCore) {
      cap += this.annexBonus(owner, 'workforceBonus');
      for (const b of this.state.buildings.values()) {
        if (b.owner === owner && b.state === 'active' && buildingDef(b.defId).workforceBonus) {
          cap += buildingDef(b.defId).workforceBonus!;
        }
      }
    }
    return cap;
  }

  /** Workers homed at a hub, plus any queued there. */
  zoneWorkerCount(owner: PlayerId, hub: Building): number {
    let n = 0;
    for (const w of this.state.workers.values()) {
      if (w.owner === owner && !w.dead && w.homeId === hub.id) n++;
    }
    if (hub.isCore) n += this.state.players[owner].workerQueue.length;
    else if (hub.workerQueueRemaining !== null) n++;
    return n;
  }

  /** Room for another worker in the hub's zone (per-zone cap, owner feedback). */
  zoneHasWorkerRoom(owner: PlayerId, hub: Building): boolean {
    return this.zoneWorkerCount(owner, hub) < this.zoneWorkerCap(owner, hub);
  }

  /** Is a Core annex upgrade researched for this player? */
  coreUpgradeDone(owner: PlayerId, upgradeId: string): boolean {
    const core = this.coreAlive(owner);
    return !!core && core.upgradesDone.includes(upgradeId);
  }

  /** All annex effects of a given kind researched on this player's Core (D2). */
  *annexEffects<K extends AnnexEffect['kind']>(
    owner: PlayerId,
    kind: K,
  ): Generator<Extract<AnnexEffect, { kind: K }>> {
    const core = this.coreAlive(owner);
    if (!core) return;
    const track = buildingDef(core.defId).upgrades;
    if (!track) return;
    for (const upId of core.upgradesDone) {
      const up = track.find((u) => u.id === upId);
      for (const eff of up?.effects ?? []) {
        if (eff.kind === kind) yield eff as Extract<AnnexEffect, { kind: K }>;
      }
    }
  }

  /** First researched annex effect of this kind, or null (single-provider effects). */
  annexEffect<K extends AnnexEffect['kind']>(owner: PlayerId, kind: K): Extract<AnnexEffect, { kind: K }> | null {
    for (const eff of this.annexEffects(owner, kind)) return eff;
    return null;
  }

  /** Completed socketed annexes on this player's Core (Core Annex System). */
  *coreAnnexes(owner: PlayerId): Generator<UpgradeDef> {
    const core = this.coreAlive(owner);
    if (!core || core.state !== 'active') return;
    const track = buildingDef(core.defId).upgrades;
    if (!track) return;
    for (const upId of core.upgradesDone) {
      const up = track.find((u) => u.id === upId);
      if (up) yield up;
    }
  }

  /** Product of all researched annex multipliers of this kind (1 when none). */
  annexMult(
    owner: PlayerId,
    kind:
      | 'coreDamageTakenMult'
      | 'coreAttackMult'
      | 'buildSpeedMult'
      | 'repairSpeedMult'
      | 'recoveryMult'
      | 'trainSpeedMult'
      | 'upgradeSpeedMult'
      | 'builderProtection'
      | 'scaffoldProtection'
      | 'carryMult'
      | 'workerRetreatSpeedMult'
      | 'workerTrainSpeedMult'
      | 'conduitCarryHaste',
  ): number {
    let m = 1;
    for (const eff of this.annexEffects(owner, kind)) m *= eff.mult;
    return m;
  }

  /** Sum of all researched annex bonuses of this kind (0 when none). */
  annexBonus(owner: PlayerId, kind: 'shelterBonus' | 'commandBonus' | 'workforceBonus' | 'rootCapacityBonus' | 'corpseStorageBonus' | 'extraBuilders'): number {
    let n = 0;
    for (const eff of this.annexEffects(owner, kind)) n += eff.amount;
    return n;
  }

  /** Grim Industry (Ossuary Ledger): a deliberate sacrifice fuels the remaining workers. */
  grantGrimIndustry(owner: PlayerId): void {
    const eff = this.annexEffect(owner, 'grimIndustry');
    if (!eff) return;
    const stacks = this.state.players[owner].annex.grimStacks;
    stacks.push(eff.duration);
    if (stacks.length > eff.maxStacks) stacks.shift();
  }

  /** Current Grim Industry gather multiplier (1 when inactive). */
  grimIndustryMult(owner: PlayerId): number {
    const eff = this.annexEffect(owner, 'grimIndustry');
    if (!eff) return 1;
    const stacks = this.state.players[owner].annex.grimStacks.length;
    return 1 + (eff.gatherMult - 1) * stacks;
  }

  /** every cell of a prospective footprint is open ground (carrier deployment) */
  footprintClear(pos: Vec2, fp: { w: number; h: number }): boolean {
    const x0 = pos.x - fp.w / 2;
    const y0 = pos.y - fp.h / 2;
    for (let x = 0; x <= fp.w; x++) {
      for (let y = 0; y <= fp.h; y++) {
        if (!this.nav.isWalkable(vec(x0 + x, y0 + y))) return false;
      }
    }
    return true;
  }

  /** inside own core territory or an owned site's build radius */
  inFriendlyTerritory(pos: Vec2, owner: PlayerId): boolean {
    const core = this.coreAlive(owner);
    if (core && dist(pos, core.pos) <= this.map.coreTerritoryRadius) return true;
    for (const s of this.state.sites) {
      if (s.owner === owner && dist(pos, s.pos) <= TUNING.buildRadius) return true;
    }
    return false;
  }

  /** valid summon location for a card's summonLocations list */
  isValidSummonPos(pos: Vec2, owner: PlayerId, locations: string[], ascension?: number): boolean {
    if (locations.includes('anywhere-friendly')) return true;
    if (locations.includes('core')) {
      const core = this.coreAlive(owner);
      if (core && dist(pos, core.pos) <= this.map.coreTerritoryRadius) return true;
    }
    if (locations.includes('controlled-site')) {
      for (const s of this.state.sites) {
        if (s.owner === owner && dist(pos, s.pos) <= TUNING.buildRadius) return true;
      }
    }
    // faction summoning structures (GDD §22.1): saplings, rift altars
    for (const b of this.state.buildings.values()) {
      if (b.owner !== owner || b.state !== 'active') continue;
      const bd = buildingDef(b.defId);
      const anchor = bd.summonAnchor;
      if (anchor && (ascension === undefined || ascension <= anchor.maxAscension)) {
        if (dist(pos, b.pos) <= anchor.radius) return true;
      }
      // Convergence Protocol: linked conduits accept Asc I–II summons while it runs
      if (
        bd.conduit &&
        this.state.players[owner].convergenceRemaining > 0 &&
        (ascension === undefined || ascension <= 2) &&
        this.state.connectedConduits.has(b.id) &&
        dist(pos, b.pos) <= 12
      ) {
        return true;
      }
    }
    return false;
  }

  // ---------------------------------------------------------------- caps

  recomputeCaps(owner: PlayerId): void {
    const p = this.state.players[owner];
    const sites = this.sitesOwned(owner);
    // workforce cap = sum of every zone's cap (Core + each active site tower)
    let wfCap = 0;
    for (const hub of this.workerHubs(owner)) wfCap += this.zoneWorkerCap(owner, hub);
    p.workforceCap = wfCap;
    let wfUsed = p.workerQueue.length;
    for (const w of this.state.workers.values()) if (w.owner === owner && !w.dead) wfUsed++;
    for (const b of this.state.buildings.values()) {
      if (b.owner === owner && b.workerQueueRemaining !== null) wfUsed++;
    }
    p.workforceUsed = wfUsed;

    let cmdCap = TUNING.commandCore + sites * TUNING.commandPerSite;
    for (let tier = 2; tier <= p.ascensionDiscovered; tier++) {
      cmdCap += TUNING.commandAscension[tier] ?? 0;
    }
    cmdCap += this.annexBonus(owner, 'commandBonus');
    p.commandCap = cmdCap;
    let cmdUsed = 0;
    for (const s of this.state.squads.values()) {
      if (s.owner === owner && !s.dead && !s.commandFree) cmdUsed += unitDef(s.defId).command;
    }
    for (const b of this.state.buildings.values()) {
      if (b.owner !== owner) continue;
      for (const q of b.queue) cmdUsed += q.costCommand;
    }
    p.commandUsed = cmdUsed;
  }

  updateAscension(owner: PlayerId): void {
    const p = this.state.players[owner];
    const sites = this.sitesOwned(owner);
    const playable = (Math.min(3, sites) + 1) as 1 | 2 | 3 | 4;
    p.ascensionPlayable = playable;
    if (playable > p.ascensionDiscovered) {
      p.ascensionDiscovered = playable;
      this.pushEvent({ type: 'AscensionUnlocked', player: owner, tier: playable });
      this.recomputeCaps(owner);
    }
  }

  // ---------------------------------------------------------------- damage

  /**
   * Apply damage to a squad. Returns actual damage dealt.
   */
  damageSquad(
    target: Squad,
    raw: number,
    opts: { isRanged?: boolean; attackerOwner?: PlayerId },
  ): number {
    const d = unitDef(target.defId);
    let dmg = raw * (1 - armorReductionOf(d.armor));
    dmg *= buffMult(target.buffs, 'damageTaken');
    if (opts.isRanged) dmg *= buffMult(target.buffs, 'rangedDamageTaken');
    // Rootwarden guardian stance: bark armor while rooted
    if (target.treeRooted && d.rootable) dmg *= d.rootable.damageTakenMult;
    if (dmg <= 0) return 0;
    // temp health absorbs first
    if (target.tempHealth > 0) {
      const absorbed = Math.min(target.tempHealth, dmg);
      target.tempHealth -= absorbed;
      dmg -= absorbed;
    }
    target.health -= dmg;
    target.lastDamagedAt = this.state.time;
    target.lastCombatAt = this.state.time;
    if (target.health <= 0 && !target.dead) this.killSquad(target);
    return dmg;
  }

  killSquad(target: Squad, opts?: { noCorpse?: boolean }): void {
    target.dead = true;
    this.state.squads.delete(target.id);
    this.state.players[target.owner].stats.unitsLost++;
    this.pushEvent({
      type: 'SquadDied',
      id: target.id,
      owner: target.owner,
      pos: { ...target.pos },
      wasBoss: target.isBoss,
      defId: target.defId,
    });
    // death record ring buffer (allyDiedNearby/escortSquadDied triggers, resurrection rituals)
    this.state.recentDeaths.push({
      defId: target.defId,
      owner: target.owner,
      pos: { ...target.pos },
      at: this.state.time,
      wasBoss: target.isBoss,
      escortOf: target.escortOf,
    });
    if (this.state.recentDeaths.length > TUNING.recentDeathBuffer) this.state.recentDeaths.shift();
    // corpse marks are generated HERE, deterministically — never from the transient
    // event list, which other systems consume mid-tick (R2)
    if (this.corpseEconomyActive && !opts?.noCorpse && !unitDef(target.defId).roleTags.includes('worker')) {
      this.state.corpseMarks.push({
        id: this.newId(),
        pos: { ...target.pos },
        remaining: TUNING.corpseMarkDuration,
        sourceOwner: target.owner,
      });
      if (this.state.corpseMarks.length > TUNING.corpseMarkCap) this.state.corpseMarks.shift();
    }
    // Black Harvest zones convert deaths inside them into resources
    for (const z of this.state.zones.values()) {
      const harvest = defs.spells.get(z.spellId)?.deathHarvest;
      if (!harvest) continue;
      if (dist(target.pos, z.pos) > z.radius) continue;
      const p = this.state.players[z.owner];
      if (this.areAllies(target.owner, z.owner)) p.power += harvest.allyPower;
      else p.essence += harvest.enemyEssence;
    }
    // escorts of a dead boss hold position (§20.8)
    if (target.isBoss) {
      for (const s of this.state.squads.values()) {
        if (s.escortOf === target.id) {
          s.escortOf = null;
          s.command = { kind: 'defend', pointA: { ...s.pos }, auto: true };
        }
      }
    }
    // squads mounted on a dead host drop beside it
    for (const s of this.state.squads.values()) {
      if (s.mountedOnSquad === target.id) {
        s.mountedOnSquad = null;
        s.pos = this.nav.nearestWalkable(s.pos);
        s.prevPos = { ...s.pos };
        s.lastOpenPos = { ...s.pos };
        s.command = { kind: 'defend', pointA: { ...s.pos }, auto: true };
      }
    }
    this.recomputeCaps(target.owner);
  }

  /** Remove a squad without death effects — temporary units expiring (shades, resurrections). */
  despawnSquad(target: Squad): void {
    target.dead = true;
    this.state.squads.delete(target.id);
    for (const s of this.state.squads.values()) {
      if (s.mountedOnSquad === target.id) {
        s.mountedOnSquad = null;
        s.pos = this.nav.nearestWalkable(s.pos);
        s.prevPos = { ...s.pos };
        s.lastOpenPos = { ...s.pos };
        s.command = { kind: 'defend', pointA: { ...s.pos }, auto: true };
      }
      if (s.escortOf === target.id) s.escortOf = null;
    }
    this.recomputeCaps(target.owner);
  }

  /** Any player fielding a corpse-economy faction this match? (computed from immutable setup) */
  get corpseEconomyActive(): boolean {
    return this.state.players.some((p) => factionDef(p.faction).corpseEconomy);
  }

  /**
   * Teleport a squad (R3): clamps distance, lands on walkable ground, clears all path
   * state so navigation restarts cleanly from the destination.
   */
  teleportSquad(s: Squad, to: Vec2, maxDist: number): void {
    const from = { ...s.pos };
    let dest = { ...to };
    const d = dist(s.pos, dest);
    if (d > maxDist) {
      const t = maxDist / d;
      dest = vec(s.pos.x + (dest.x - s.pos.x) * t, s.pos.y + (dest.y - s.pos.y) * t);
    }
    dest = this.nav.nearestWalkable(dest);
    s.pos = { ...dest };
    s.prevPos = { ...dest }; // no interpolation streak across the jump
    s.lastOpenPos = { ...dest };
    s.path = null;
    s.pathGoal = null;
    s.moveTarget = null;
    s.stuckTime = 0;
    s.progressGoal = null;
    s.bestRouteRemaining = Infinity;
    s.progressNavRevision = this.nav.revision;
    s.progressRouteSignature = 0;
    s.repathCooldown = 0;
    this.pushEvent({ type: 'SquadTeleported', id: s.id, from, to: { ...dest } });
  }

  /** Stun a squad with a recoverable physical knockdown pose. */
  knockdownSquad(target: Squad, duration: number): void {
    if (target.dead || duration <= 0) return;
    target.knockdownRemaining = Math.max(target.knockdownRemaining, duration);
    target.knockdownTotal = Math.max(target.knockdownTotal, duration);
    target.rootedRemaining = Math.max(target.rootedRemaining, duration);
    target.moveTarget = null;
    target.path = null;
    target.pathGoal = null;
    target.engaged = false;
    target.telegraph = null;
    target.charge = null;
    this.pushEvent({
      type: 'SquadKnockedDown',
      id: target.id,
      owner: target.owner,
      pos: { ...target.pos },
      duration,
    });
  }

  damageWorker(target: WorkerEnt, raw: number): number {
    let dmg = raw; // workers unarmored
    // Royal Works: builders under scaffold cover take reduced damage
    if (
      target.state === 'building' ||
      target.state === 'repairing' ||
      target.state === 'toBuild' ||
      target.state === 'toRepair'
    ) {
      dmg *= this.annexMult(target.owner, 'builderProtection');
    }
    target.health -= dmg;
    target.lastDamagedAt = this.state.time;
    // damage interrupts a Deep Recall channel
    target.recallChannelRemaining = 0;
    // Phase Barrier: the first worker who would die near the Core is translated into
    // shelter at 1 health instead (per-cooldown)
    if (target.health <= 0 && !target.dead) {
      const p = this.state.players[target.owner];
      const save = this.annexEffect(target.owner, 'workerDeathSave');
      const core = this.coreAlive(target.owner);
      if (
        save &&
        core &&
        p.annex.deathSaveCooldown <= 0 &&
        dist(target.pos, core.pos) <= this.map.coreTerritoryRadius &&
        p.shelterOccupants.length < p.shelterCapacity
      ) {
        p.annex.deathSaveCooldown = save.cooldown;
        target.health = 1;
        target.pos = { ...core.pos };
        target.prevPos = { ...core.pos };
        target.path = null;
        target.state = 'sheltered';
        target.carrying = null;
        target.depositId = null;
        p.shelterOccupants.push(target.id);
        this.pushEvent({
          type: 'AbilityTriggered',
          id: core.id,
          abilityId: 'phase-translation',
          pos: { ...core.pos },
          label: 'Phase Translation',
        });
        return dmg;
      }
    }
    if (target.health <= 0 && !target.dead) {
      target.dead = true;
      this.state.workers.delete(target.id);
      const p = this.state.players[target.owner];
      const shelterIdx = p.shelterOccupants.indexOf(target.id);
      if (shelterIdx >= 0) p.shelterOccupants.splice(shelterIdx, 1);
      this.pushEvent({ type: 'WorkerDied', id: target.id, owner: target.owner, pos: { ...target.pos }, defId: target.defId });
      this.recomputeCaps(target.owner);
    } else {
      this.pushEvent({ type: 'RaidWarning', player: target.owner, pos: { ...target.pos } });
    }
    return dmg;
  }

  damageBuilding(
    target: Building,
    raw: number,
    opts: { structureMult?: number; isSiege?: boolean; attackerOwner?: PlayerId; fireTag?: boolean },
  ): number {
    if (opts.fireTag) target.fireSuppressedRemaining = 20;
    const d = buildingDef(target.defId);
    let dmg = raw * (opts.structureMult ?? 1) * (1 - armorReductionOf(d.armor));
    if (target.isCore) dmg *= this.annexMult(target.owner, 'coreDamageTakenMult');
    // walls are fragile while raising — break them during the build-up window
    if (d.kind === 'wall' && target.state !== 'active') dmg *= TUNING.wallConstructingDamageTakenMult;
    // Royal Works: incomplete (non-wall) buildings gain scaffold armor
    if (d.kind !== 'wall' && target.state !== 'active') dmg *= this.annexMult(target.owner, 'scaffoldProtection');
    if (dmg <= 0) return 0;
    // Phase Barrier: the Core's regenerating phase shield absorbs first
    if (target.isCore) {
      const annex = this.state.players[target.owner].annex;
      if (annex.coreShield > 0) {
        const absorbed = Math.min(annex.coreShield, dmg);
        annex.coreShield -= absorbed;
        dmg -= absorbed;
        if (dmg <= 0) {
          target.lastDamagedAt = this.state.time;
          return absorbed;
        }
      }
    }
    if (target.tempHealth > 0) {
      const absorbed = Math.min(target.tempHealth, dmg);
      target.tempHealth -= absorbed;
      dmg -= absorbed;
    }
    target.health -= dmg;
    target.lastDamagedAt = this.state.time;
    if (opts.isSiege) target.lastSiegeDamagedAt = this.state.time;
    if (target.isCore) {
      if (opts.attackerOwner !== undefined) {
        this.state.players[opts.attackerOwner].stats.coreDamageDealt += dmg;
      }
      this.pushEvent({
        type: 'CoreDamaged',
        player: target.owner,
        healthFraction: Math.max(0, target.health) / target.maxHealth,
      });
      // Last Course of Stone (once, at 30%)
      if (!target.lastCourseFired && target.health / target.maxHealth <= TUNING.lastCourseThreshold) {
        target.lastCourseFired = true;
        target.repairResist = TUNING.lastCourseRepairResist;
        this.pushEvent({
          type: 'AbilityTriggered',
          id: target.id,
          abilityId: 'last-course',
          pos: { ...target.pos },
          label: 'Last Course of Stone',
        });
      }
    }
    if (target.health <= 0 && target.state !== 'destroyed') this.destroyBuilding(target);
    return dmg;
  }

  destroyBuilding(target: Building): void {
    const d = buildingDef(target.defId);
    const awakening = d.traits?.find((trait) => trait.kind === 'finalAwakening');
    if (awakening) this.spawnSquad(awakening.unitId, target.owner, target.pos, { temporarySeconds: awakening.duration });
    const breach = d.traits?.find((trait) => trait.kind === 'breachRoot');
    if (breach && (!breach.disabledByFire || target.fireSuppressedRemaining <= 0)) {
      for (const squad of this.state.squads.values()) {
      if (this.areHostile(target.owner, squad.owner) && dist(squad.pos, target.pos) <= breach.radius) {
          squad.rootedRemaining = Math.max(squad.rootedRemaining, breach.duration);
        }
      }
    }
    // Place wall riders while the wall and its protected side are still known. Clearing
    // the building first stranded riders at the former footprint with stale path state.
    if (d.kind === 'wall') {
      for (const s of this.state.squads.values()) {
        if (s.mountedOnWall !== target.id) continue;
        dismountWallSquad(this, s);
        s.command = { kind: 'defend', pointA: { ...s.pos }, auto: true };
      }
    }
    target.state = 'destroyed';
    this.state.buildings.delete(target.id);
    this.rehomeWorkers(target.id, target.owner);
    this.stampBuildingNav(target, -1);
    // refund 50% of queued production, free reserved command
    const p = this.state.players[target.owner];
    for (const q of target.queue) {
      p.power += Math.round(q.costPower * TUNING.queueDestroyRefund);
      p.essence += Math.round(q.costEssence * TUNING.queueDestroyRefund);
    }
    target.queue = [];
    // free active-copy on the owning card
    if (target.cardId) {
      const cr = p.cards[target.cardId];
      if (cr) cr.activeCopies = Math.max(0, cr.activeCopies - 1);
    }
    this.pushEvent({
      type: 'BuildingDestroyed',
      id: target.id,
      owner: target.owner,
      pos: { ...target.pos },
      defId: target.defId,
    });
    // gate collapse: falling barrier stuns small/medium squads beside it (Oathbound Gate)
    if (d.gate) {
      for (const s of this.state.squads.values()) {
        if (!this.areHostile(target.owner, s.owner)) continue;
        const sd = unitDef(s.defId);
        if (sd.sizeTag !== 'small' && sd.sizeTag !== 'medium') continue;
        if (dist(s.pos, target.pos) <= d.gate.radius) {
          s.rootedRemaining = Math.max(s.rootedRemaining, d.gate.stunSeconds);
        }
      }
    }
    // site tower down → the site falls neutral (counterplay to capture)
    if (d.isSiteTower && target.siteId) {
      const site = this.state.sites.find((s) => s.id === target.siteId);
      if (site && site.owner === target.owner) {
        site.owner = -1;
        site.control = 0;
        site.meter = 0;
        site.meterPlayer = -1;
        this.pushEvent({ type: 'SiteLost', site: site.id, loser: target.owner });
        this.pushEvent({ type: 'SiteNeutralized', site: site.id });
        this.updateAscension(target.owner);
      }
    }
    // castle wall down → dismount garrison, ruin becomes claimable again
    if (d.kind === 'wall') {
      const ruin = this.state.ruins.find((r) => r.buildingId === target.id);
      if (ruin) {
        ruin.buildingId = null;
        // combat destruction locks the spot for a while — no instant rebuild wars
        ruin.cooldownUntil = this.state.time + TUNING.wallRebuildCooldownSeconds;
      }
    }
    this.recomputeCaps(target.owner);
    if (target.isCore && this.state.phase === 'running' && !this.teamHasCore(this.teamOf(target.owner))) {
      this.state.phase = 'ended';
      this.state.winningTeam = opposingTeam(this.teamOf(target.owner));
      this.pushEvent({ type: 'MatchEnded', winningTeam: this.state.winningTeam });
    }
  }

  damageMine(target: Mine, raw: number): void {
    target.health -= raw;
    if (target.health <= 0 && !target.dead) {
      target.dead = true;
      this.state.mines.delete(target.id);
    }
  }

  // ---------------------------------------------------------------- command intake

  dispatch(cmd: SimCommand): void {
    this.queue.push({ command: cmd });
  }

  dispatchWithResult(cmd: SimCommand, onResult: (result: CommandResult) => void): void {
    this.queue.push({ command: cmd, onResult });
  }

  private drainCommands(): void {
    // player commands first, then AI (stable order, §8.3)
    const sorted = this.queue
      .map((entry, i) => ({ entry, i }))
      .sort((a, b) => (a.entry.command.player - b.entry.command.player) || (a.i - b.i));
    this.queue = [];
    for (const { entry } of sorted) {
      const validation = this.validateCommand(entry.command);
      if (validation.status === 'denied') {
        entry.onResult?.(validation);
        continue;
      }
      try {
        const eventStart = this.state.events.length;
        this.applyCommand(entry.command);
        const cardDenial = entry.command.kind === 'PlayCard'
          ? this.state.events.slice(eventStart).find((event) => event.type === 'CardDenied' && event.player === entry.command.player)
          : undefined;
        entry.onResult?.(cardDenial && cardDenial.type === 'CardDenied'
          ? { status: 'denied', reason: cardDenial.reason }
          : { status: 'applied' });
      } catch {
        entry.onResult?.({ status: 'denied', reason: 'commandFailed' });
      }
    }
  }

  private validateCommand(cmd: SimCommand): CommandResult {
    const denied = (reason: string): CommandResult => ({ status: 'denied', reason });
    const p = this.state.players[cmd.player];
    const inMap = (point: Vec2 | undefined): boolean =>
      point === undefined ||
      (Number.isFinite(point.x) && Number.isFinite(point.y) && point.x >= 0 && point.y >= 0 && point.x <= this.map.width && point.y <= this.map.height);
    const ownedBuilding = (id: EntId): Building | null => {
      const building = this.state.buildings.get(id);
      return building?.owner === cmd.player ? building : null;
    };
    const ownsAnySquad = (ids: EntId[]): boolean => ids.some((id) => this.state.squads.get(id)?.owner === cmd.player);
    const ownsAnyWorker = (ids: EntId[]): boolean => ids.some((id) => this.state.workers.get(id)?.owner === cmd.player);

    switch (cmd.kind) {
      case 'PlayCard': {
        if (!defs.cards.has(cmd.cardId)) return denied('unknownCard');
        if (!inMap(cmd.targetPos)) return denied('outOfMap');
        const reason = canPlayBase(this, cmd.player, cmd.cardId);
        return reason === 'ok' ? { status: 'applied' } : denied(reason);
      }
      case 'IssueCommand':
        if (!cmd.squadIds.length || !ownsAnySquad(cmd.squadIds)) return denied('noOwnedEntities');
        return inMap(cmd.targetPos) ? { status: 'applied' } : denied('outOfMap');
      case 'SetAllocation':
        return Number.isFinite(cmd.power) && cmd.power >= 0 && cmd.power <= 100 ? { status: 'applied' } : denied('invalidAllocation');
      case 'SetAutoWorker':
      case 'RecallWorkers':
      case 'ResumeWork':
        return { status: 'applied' };
      case 'QueueWorker': {
        const core = this.coreAlive(cmd.player);
        if (!core) return denied('coreDestroyed');
        if (p.workforceUsed >= p.workforceCap || !this.zoneHasWorkerRoom(cmd.player, core)) return denied('workforceFull');
        return p.power >= TUNING.workerCost ? { status: 'applied' } : denied('insufficientPower');
      }
      case 'QueueUnit': {
        const building = ownedBuilding(cmd.buildingId);
        if (!building || building.state !== 'active') return denied('invalidBuilding');
        const reason = this.unitQueueDenial(building, cmd.unitId);
        return reason ? denied(reason) : { status: 'applied' };
      }
      case 'CancelQueueItem': {
        const building = ownedBuilding(cmd.buildingId);
        return building?.queue[cmd.index] ? { status: 'applied' } : denied('invalidQueueItem');
      }
      case 'ToggleAutoProduce': {
        const building = ownedBuilding(cmd.buildingId);
        if (!building) return denied('invalidBuilding');
        const valid = buildingDef(building.defId).roster?.entries.some((entry) => entry.unitId === cmd.unitId);
        if (!valid) return denied('invalidBuilding');
        if (building.autoProduce[cmd.unitId]) return { status: 'applied' };
        const reason = this.unitQueueDenial(building, cmd.unitId);
        return reason ? denied(reason) : { status: 'applied' };
      }
      case 'WorkerRetreat':
        return ownsAnyWorker(cmd.workerIds) ? { status: 'applied' } : denied('noOwnedEntities');
      case 'RepairOrder':
        return ownedBuilding(cmd.buildingId) && ownsAnyWorker(cmd.workerIds) ? { status: 'applied' } : denied('invalidOwnership');
      case 'SetRally':
        return ownedBuilding(cmd.buildingId) && inMap(cmd.pos) ? { status: 'applied' } : denied('invalidRally');
      case 'BuyUpgrade':
      {
        const building = ownedBuilding(cmd.buildingId);
        if (!building || building.state !== 'active' || building.upgradeDone || building.upgradeRemaining > 0) return denied('invalidUpgrade');
        const upgrade = buildingDef(building.defId).upgrade;
        if (!upgrade) return denied('invalidUpgrade');
        if (p.power < upgrade.cost.power) return denied('insufficientPower');
        return p.essence < upgrade.cost.essence ? denied('insufficientEssence') : { status: 'applied' };
      }
      case 'BuyCoreUpgrade': {
        const building = ownedBuilding(cmd.buildingId);
        if (!building || building.state !== 'active' || building.upgradeInProgress || building.upgradesDone.includes(cmd.upgradeId)) return denied('invalidUpgrade');
        const upgrade = buildingDef(building.defId).upgrades?.find((item) => item.id === cmd.upgradeId);
        if (!upgrade) return denied('invalidUpgrade');
        if (p.power < upgrade.cost.power) return denied('insufficientPower');
        return p.essence < upgrade.cost.essence ? denied('insufficientEssence') : { status: 'applied' };
      }
      case 'QueueTowerWorker': {
        const building = ownedBuilding(cmd.buildingId);
        if (!building || building.state !== 'active' || !buildingDef(building.defId).producesWorkers || building.workerQueueRemaining !== null) return denied('invalidBuilding');
        if (p.workforceUsed >= p.workforceCap || !this.zoneHasWorkerRoom(cmd.player, building)) return denied('workforceFull');
        return p.power >= TUNING.workerCost ? { status: 'applied' } : denied('insufficientPower');
      }
      case 'CancelConstruction': {
        const building = ownedBuilding(cmd.buildingId);
        return building && (building.state === 'blueprint' || building.state === 'constructing') ? { status: 'applied' } : denied('invalidBuilding');
      }
      case 'ToggleBuildingMode':
      case 'ToggleTowerWorker':
        return ownedBuilding(cmd.buildingId) ? { status: 'applied' } : denied('invalidOwnership');
      case 'ClaimRuin': {
        const ruin = this.state.ruins.find((candidate) => candidate.id === cmd.ruinId && candidate.buildingId === null);
        if (!ruin) return denied('invalidRuin');
        // grouped ruins (base ring) raise together via BuildBaseWall — never piecemeal
        if (ruin.group !== null) return denied('invalidRuin');
        if (this.state.time < ruin.cooldownUntil) return denied('invalidRuin');
        if (p.power < ruin.cost) return denied('insufficientPower');
        return [...this.state.squads.values()].some((squad) => squad.owner === cmd.player && dist(squad.pos, ruin.pos) <= 9)
          ? { status: 'applied' } : denied('noOwnedEntities');
      }
      case 'BuildBaseWall': {
        if (cmd.group !== this.baseWallGroup(cmd.player)) return denied('invalidRuin');
        const remaining = this.state.ruins.filter(
          (r) => r.group === cmd.group && r.buildingId === null && this.state.time >= r.cooldownUntil,
        );
        if (remaining.length === 0) return denied('invalidRuin');
        const total = remaining.reduce((sum, r) => sum + r.cost, 0);
        return p.power >= total ? { status: 'applied' } : denied('insufficientPower');
      }
      case 'MountWall':
        return ownedBuilding(cmd.buildingId) && ownsAnySquad(cmd.squadIds) ? { status: 'applied' } : denied('invalidOwnership');
      case 'MountSquad':
        return this.state.squads.get(cmd.hostId)?.owner === cmd.player && ownsAnySquad(cmd.squadIds) ? { status: 'applied' } : denied('invalidOwnership');
      case 'Dismount':
        return cmd.squadIds.some((id) => {
          const squad = this.state.squads.get(id);
          return squad?.owner === cmd.player && (squad.mountedOnSquad !== null || squad.mountedOnWall !== null);
        }) ? { status: 'applied' } : denied('invalidOwnership');
      case 'Sacrifice':
      {
        const squad = this.state.squads.get(cmd.squadId);
        return squad?.owner === cmd.player && !squad.isBoss ? { status: 'applied' } : denied('invalidOwnership');
      }
      case 'DeployCarrier': {
        const squad = this.state.squads.get(cmd.squadId);
        if (!squad || squad.owner !== cmd.player) return denied('invalidOwnership');
        const dep = unitDef(squad.defId).deploysInto;
        if (!dep) return denied('notDeployable');
        if (!this.inFriendlyTerritory(squad.pos, cmd.player)) return denied('notControlledTerritory');
        if (!this.footprintClear(squad.pos, buildingDef(dep.buildingId).footprint)) return denied('blockedGround');
        return { status: 'applied' };
      }
      case 'ToggleRoot': {
        const squad = this.state.squads.get(cmd.squadId);
        if (!squad || squad.owner !== cmd.player) return denied('invalidOwnership');
        if (!unitDef(squad.defId).rootable) return denied('notRootable');
        if (squad.uprootRemaining > 0) return denied('uprooting');
        return { status: 'applied' };
      }
      case 'DeepRecall': {
        const annexDef = [...this.coreAnnexes(cmd.player)].find((u) => u.deepRecall);
        if (!annexDef) return denied('noAnnex');
        return p.annex.deepRecallCooldown <= 0 ? { status: 'applied' } : denied('onCooldown');
      }
      case 'TransferWorkers': {
        const annexDef = [...this.coreAnnexes(cmd.player)].find((u) => u.workerTransfer);
        if (!annexDef) return denied('noAnnex');
        if (p.annex.transferCooldown > 0) return denied('onCooldown');
        const target = ownedBuilding(cmd.buildingId);
        if (!target || target.state !== 'active' || target.isCore) return denied('invalidBuilding');
        if (!buildingDef(target.defId).conduit || !this.state.connectedConduits.has(target.id)) return denied('notConnected');
        return p.shelterOccupants.length > 0 ? { status: 'applied' } : denied('noShelteredWorkers');
      }
      case 'SetTithe': {
        const annexDef = [...this.coreAnnexes(cmd.player)].find((u) => u.bloodTithe);
        if (!annexDef) return denied('noAnnex');
        return p.annex.apotheosisUsed || p.annex.apotheosis ? denied('apotheosisSpent') : { status: 'applied' };
      }
      case 'SetEchoArmed': {
        const annexDef = [...this.coreAnnexes(cmd.player)].find((u) => u.echo);
        return annexDef ? { status: 'applied' } : denied('noAnnex');
      }
      case 'ChooseApotheosis': {
        const annexDef = [...this.coreAnnexes(cmd.player)].find((u) => u.bloodTithe);
        if (!annexDef) return denied('noAnnex');
        return annexDef.bloodTithe!.forms.some((f) => f.form === cmd.form) && !p.annex.apotheosisUsed
          ? { status: 'applied' }
          : denied('invalidForm');
      }
      case 'CheatResources':
      case 'CheatAscension':
      case 'CheatCorpse':
      case 'CheatFinishRituals':
      case 'EpicBattle':
        return { status: 'applied' };
    }
  }

  private applyCommand(cmd: SimCommand): void {
    const p = this.state.players[cmd.player];
    switch (cmd.kind) {
      case 'PlayCard':
        processPlayCard(this, cmd);
        break;
      case 'IssueCommand': {
        const squads: Squad[] = [];
        for (const id of cmd.squadIds) {
          const s = this.state.squads.get(id);
          if (!s || s.owner !== cmd.player) continue;
          squads.push(s);
        }
        // Formation slotting for group moves. Every squad used to receive the *identical*
        // targetPos, so an army converged on one point and its arrival shape was whatever crowd
        // separation happened to produce. Only for positional orders with no pinned entity —
        // an attack on a specific target, or a capture, still means "all of you, that thing".
        const formation =
          cmd.targetPos && cmd.targetEntity === undefined && (cmd.command === 'move' || cmd.command === 'attack')
            ? planFormation(this, squads, cmd.targetPos)
            : null;
        if (formation) {
          for (const { squad, target } of formation) {
            this.issueSquadCommand(squad, cmd.command, target, cmd.targetEntity);
          }
          // Group speed matching: without it the army strings out by unit speed and arrives in
          // dribs. Capped at the group's slowest member until each squad reaches its slot.
          let slowest = Infinity;
          for (const { squad } of formation) slowest = Math.min(slowest, unitDef(squad.defId).moveSpeed);
          for (const { squad } of formation) squad.groupSpeedCap = slowest;
        } else {
          for (const s of squads) {
            s.groupSpeedCap = null;
            this.issueSquadCommand(s, cmd.command, cmd.targetPos, cmd.targetEntity);
          }
        }
        break;
      }
      case 'SetAllocation':
        p.allocationPower = Math.max(0, Math.min(100, Math.round(cmd.power)));
        break;
      case 'SetAutoWorker':
        p.autoWorker = cmd.enabled;
        break;
      case 'QueueWorker': {
        if (p.workforceUsed >= p.workforceCap) break;
        if (p.power < TUNING.workerCost) break;
        const core = this.coreAlive(cmd.player);
        if (!core || !this.zoneHasWorkerRoom(cmd.player, core)) break;
        p.power -= TUNING.workerCost;
        p.workerQueue.push({ remaining: TUNING.workerTrainTime });
        this.recomputeCaps(cmd.player);
        break;
      }
      case 'QueueUnit': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (!b || b.owner !== cmd.player || b.state !== 'active') break;
        this.tryEnqueueUnit(b, cmd.unitId);
        break;
      }
      case 'CancelQueueItem': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (!b || b.owner !== cmd.player) break;
        const item = b.queue[cmd.index];
        if (!item) break;
        p.power += item.costPower;
        p.essence += item.costEssence;
        b.queue.splice(cmd.index, 1);
        this.recomputeCaps(cmd.player);
        break;
      }
      case 'ToggleAutoProduce': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (!b || b.owner !== cmd.player) break;
        b.autoProduce[cmd.unitId] = !b.autoProduce[cmd.unitId];
        break;
      }
      case 'RecallWorkers': {
        // per-hub recall (owner request): no hubId = every hub (H hotkey behavior)
        const hubs = this.workerHubs(cmd.player).filter((h) => cmd.hubId === undefined || h.id === cmd.hubId);
        if (hubs.length === 0) break;
        for (const h of hubs) h.recallActive = true;
        const hubIds = new Set(hubs.map((h) => h.id));
        for (const w of this.state.workers.values()) {
          if (w.owner !== cmd.player || w.state === 'sheltered' || w.state === 'toShelter') continue;
          // global recall also sweeps up homeless workers
          if ((cmd.hubId === undefined && w.homeId === null) || (w.homeId !== null && hubIds.has(w.homeId))) {
            w.state = 'toShelter';
            w.path = null;
          }
        }
        break;
      }
      case 'ResumeWork': {
        for (const h of this.workerHubs(cmd.player)) {
          if (cmd.hubId === undefined || h.id === cmd.hubId) h.recallActive = false;
        }
        break;
      }
      case 'WorkerRetreat': {
        for (const id of cmd.workerIds) {
          const w = this.state.workers.get(id);
          if (w && w.owner === cmd.player && w.state !== 'sheltered') {
            w.state = 'toShelter';
            w.path = null;
          }
        }
        break;
      }
      case 'RepairOrder': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (!b || b.owner !== cmd.player) break;
        for (const id of cmd.workerIds) {
          const w = this.state.workers.get(id);
          if (!w || w.owner !== cmd.player || w.state === 'sheltered') continue;
          w.repairTargetId = b.id;
          w.state = 'toRepair';
          w.path = null;
        }
        break;
      }
      case 'SetRally': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (b && b.owner === cmd.player) b.rally = { ...cmd.pos };
        break;
      }
      case 'BuyUpgrade': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (!b || b.owner !== cmd.player || b.state !== 'active' || b.upgradeDone || b.upgradeRemaining > 0)
          break;
        const up = buildingDef(b.defId).upgrade;
        if (!up) break;
        if (p.power < up.cost.power || p.essence < up.cost.essence) break;
        p.power -= up.cost.power;
        p.essence -= up.cost.essence;
        b.upgradeRemaining = TUNING.upgradeResearchSeconds;
        break;
      }
      case 'BuyCoreUpgrade': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (!b || b.owner !== cmd.player || b.state !== 'active') break;
        if (b.upgradeInProgress) break;
        if (b.upgradesDone.includes(cmd.upgradeId)) break;
        const up = buildingDef(b.defId).upgrades?.find((u) => u.id === cmd.upgradeId);
        if (!up) break;
        if (p.power < up.cost.power || p.essence < up.cost.essence) break;
        p.power -= up.cost.power;
        p.essence -= up.cost.essence;
        // socketed annexes carry their own build times (Core Annex System)
        b.upgradeInProgress = { id: up.id, remaining: up.buildTime ?? TUNING.upgradeResearchSeconds };
        break;
      }
      case 'QueueTowerWorker': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (!b || b.owner !== cmd.player || b.state !== 'active') break;
        if (!buildingDef(b.defId).producesWorkers) break;
        if (b.workerQueueRemaining !== null) break; // one at a time
        if (p.workforceUsed >= p.workforceCap) break;
        if (!this.zoneHasWorkerRoom(cmd.player, b)) break;
        if (p.power < TUNING.workerCost) break;
        p.power -= TUNING.workerCost;
        b.workerQueueRemaining = TUNING.workerTrainTime;
        this.recomputeCaps(cmd.player);
        break;
      }
      case 'CancelConstruction': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (!b || b.owner !== cmd.player) break;
        if (b.state !== 'blueprint' && b.state !== 'constructing') break;
        if (b.cardId) {
          const cd = cardDef(b.cardId);
          p.power += Math.round(cd.cost.power * TUNING.blueprintCancelRefund);
          p.essence += Math.round(cd.cost.essence * TUNING.blueprintCancelRefund);
        }
        this.destroyBuildingSilently(b);
        break;
      }
      case 'BuildBaseWall': {
        if (cmd.group !== this.baseWallGroup(cmd.player)) break;
        const remaining = this.state.ruins.filter(
          (r) => r.group === cmd.group && r.buildingId === null && this.state.time >= r.cooldownUntil,
        );
        if (remaining.length === 0) break;
        const total = remaining.reduce((sum, r) => sum + r.cost, 0);
        if (p.power < total) break;
        p.power -= total;
        // the whole ring (all segments + the central gate) raises at once (owner request)
        for (const ruin of remaining) {
          const wall = this.spawnBuilding(factionDef(p.faction).wallDefId, cmd.player, ruin.pos, {
            gateSpan: ruin.gateSpan,
            rot: ruin.rot,
          });
          ruin.buildingId = wall.id;
        }
        break;
      }
      case 'ClaimRuin': {
        const ruin = this.state.ruins.find((r) => r.id === cmd.ruinId);
        if (!ruin || ruin.buildingId !== null) break;
        if (ruin.group !== null) break; // base ring builds via BuildBaseWall only
        if (this.state.time < ruin.cooldownUntil) break;
        if (p.power < ruin.cost) break;
        // requires one of the player's squads near the ruin
        let near = false;
        for (const s of this.state.squads.values()) {
          if (s.owner === cmd.player && dist(s.pos, ruin.pos) <= 9) {
            near = true;
            break;
          }
        }
        if (!near) break;
        p.power -= ruin.cost;
        const wall = this.spawnBuilding(factionDef(p.faction).wallDefId, cmd.player, ruin.pos, {
          gateSpan: ruin.gateSpan,
          rot: ruin.rot,
        });
        ruin.buildingId = wall.id;
        break;
      }
      case 'MountWall': {
        const wall = this.state.buildings.get(cmd.buildingId);
        if (!wall || wall.owner !== cmd.player || wall.state !== 'active') break;
        const wd = buildingDef(wall.defId);
        const slots = wallMountCapacity(wd.mountSlots);
        if (slots <= 0) break;
        let mountedCount = 0;
        for (const s of this.state.squads.values()) if (s.mountedOnWall === wall.id) mountedCount++;
        for (const id of cmd.squadIds) {
          if (mountedCount >= slots) break;
          const s = this.state.squads.get(id);
          if (!s || s.owner !== cmd.player || s.mountedOnWall !== null || s.mountedOnSquad !== null) continue;
          const sd = unitDef(s.defId);
          if ((sd.attack?.range ?? 0) <= 4) continue; // ranged squads only
          if (dist(s.pos, wall.pos) > 8) continue;
          s.mountedOnWall = wall.id;
          s.escortOf = null;
          s.pos = wallMountSlotPosition(wall, mountedCount);
          s.prevPos = { ...s.pos };
          s.path = null;
          s.pathGoal = null;
          s.moveTarget = null;
          s.targetId = null;
          s.engaged = false;
          s.charge = null;
          s.telegraph = null;
          s.repathCooldown = 0;
          s.stuckTime = 0;
          s.progressGoal = null;
          s.bestRouteRemaining = Infinity;
          s.progressNavRevision = this.nav.revision;
          s.progressRouteSignature = 0;
          s.breachResume = null;
          s.command = { kind: 'defend', pointA: { ...s.pos }, auto: true };
          mountedCount++;
        }
        break;
      }
      case 'ToggleTowerWorker': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (b && b.owner === cmd.player) b.workerAuto = !b.workerAuto;
        break;
      }
      case 'MountSquad': {
        const host = this.state.squads.get(cmd.hostId);
        if (!host || host.owner !== cmd.player) break;
        const slots = unitDef(host.defId).mountSlots ?? 0;
        if (slots <= 0) break;
        let mounted = 0;
        for (const s of this.state.squads.values()) if (s.mountedOnSquad === host.id) mounted++;
        for (const id of cmd.squadIds) {
          if (mounted >= slots) break;
          const s = this.state.squads.get(id);
          if (!s || s.owner !== cmd.player || s.id === host.id) continue;
          if (s.mountedOnSquad !== null || s.mountedOnWall !== null) continue;
          if (!unitDef(s.defId).mountableOn?.includes(host.defId)) continue;
          if (dist(s.pos, host.pos) > TUNING.mountRange) continue;
          s.mountedOnSquad = host.id;
          s.escortOf = null;
          s.path = null;
          s.pathGoal = null;
          s.moveTarget = null;
          s.targetId = null;
          s.engaged = false;
          s.charge = null;
          s.telegraph = null;
          s.repathCooldown = 0;
          s.stuckTime = 0;
          s.progressGoal = null;
          s.bestRouteRemaining = Infinity;
          s.progressNavRevision = this.nav.revision;
          s.progressRouteSignature = 0;
          s.breachResume = null;
          s.command = { kind: 'defend', pointA: { ...host.pos }, auto: true };
          mounted++;
        }
        break;
      }
      case 'Dismount': {
        for (const id of cmd.squadIds) {
          const s = this.state.squads.get(id);
          if (!s || s.owner !== cmd.player) continue;
          if (!dismountWallSquad(this, s)) {
            if (s.mountedOnSquad === null) continue;
            s.mountedOnSquad = null;
            s.pos = this.nav.nearestWalkable(s.pos, 12, s.owner);
            s.prevPos = { ...s.pos };
            s.lastOpenPos = { ...s.pos };
            s.path = null;
            s.pathGoal = null;
            s.moveTarget = null;
            s.targetId = null;
            s.engaged = false;
          }
          s.command = { kind: 'defend', pointA: { ...s.pos }, auto: true };
        }
        break;
      }
      case 'Sacrifice': {
        const s = this.state.squads.get(cmd.squadId);
        if (!s || s.owner !== cmd.player || s.isBoss) break;
        // Devouring Gate: sacrificing at the gate stores Devoured Mass (boss discount)
        const receiver = cmd.receiverId !== undefined ? this.state.buildings.get(cmd.receiverId) : undefined;
        if (receiver && receiver.owner === cmd.player && buildingDef(receiver.defId).gate) {
          if (dist(s.pos, receiver.pos) <= 10) p.devouredMass = Math.min(2, p.devouredMass + 1);
        }
        this.killSquad(s);
        // deliberate sacrifice fuels Grim Industry (Ossuary Ledger)
        this.grantGrimIndustry(cmd.player);
        break;
      }
      case 'ToggleBuildingMode': {
        const b = this.state.buildings.get(cmd.buildingId);
        if (b && b.owner === cmd.player) {
          const bd = buildingDef(b.defId);
          if (bd.cruelProduction) {
            const workers = [...this.state.workers.values()]
              .filter((w) => w.owner === cmd.player && w.state !== 'sheltered')
              .sort((a, c) => dist(a.pos, b.pos) - dist(c.pos, b.pos) || a.id - c.id);
            if (workers.length > 5 && b.queue[0]) {
              this.damageWorker(workers[0], workers[0].health + workers[0].maxHealth);
              b.queue[0].remaining = Math.max(0, b.queue[0].remaining - 25);
              b.traitCounters.bloodArmor = 1;
              this.pushEvent({ type: 'AbilityTriggered', id: b.id, abilityId: 'cruel-production', pos: { ...b.pos }, label: 'Cruel Production' });
            }
          } else {
            b.modeActive = !b.modeActive;
            const doctrine = bd.traits?.find((trait) => trait.kind === 'doctrine');
            if (doctrine && b.upgradeDone) {
              const selected = b.modeActive ? doctrine.onUnitId : doctrine.offUnitId;
              b.autoProduce[doctrine.offUnitId] = selected === doctrine.offUnitId;
              b.autoProduce[doctrine.onUnitId] = selected === doctrine.onUnitId;
            }
          }
        }
        break;
      }
      case 'DeployCarrier': {
        const s = this.state.squads.get(cmd.squadId);
        if (!s || s.owner !== cmd.player) break;
        const dep = unitDef(s.defId).deploysInto;
        if (!dep) break;
        if (!this.inFriendlyTerritory(s.pos, cmd.player)) break;
        const bd = buildingDef(dep.buildingId);
        if (!this.footprintClear(s.pos, bd.footprint)) break;
        // one-active rule: deploying beyond the annex's cap withers the oldest structure
        const deployed: Building[] = [];
        for (const ob of this.state.buildings.values()) {
          if (ob.owner === cmd.player && ob.defId === dep.buildingId && ob.witherRemaining === null) deployed.push(ob);
        }
        deployed.sort((a, b) => a.id - b.id);
        while (deployed.length >= dep.maxDeployed) {
          const oldest = deployed.shift()!;
          if (dep.witherSeconds <= 0) this.destroyBuilding(oldest);
          else oldest.witherRemaining = dep.witherSeconds;
        }
        const at = { ...s.pos };
        const sourceAnnexId = s.sourceAnnexId ?? undefined;
        this.despawnSquad(s); // the carrier becomes the structure — no death effects
        const site = this.state.sites.find((x) => x.owner === cmd.player && dist(x.pos, at) <= TUNING.buildRadius);
        const built = this.spawnBuilding(dep.buildingId, cmd.player, at, {
          siteId: site?.id ?? null,
          annexSourceId: sourceAnnexId,
        });
        // free deployed structures assemble by themselves (selfBuilding + buildTime)
        built.state = 'constructing';
        this.pushEvent({
          type: 'AbilityTriggered',
          id: built.id,
          abilityId: 'annex-deploy',
          pos: { ...at },
          label: dep.label,
        });
        break;
      }
      case 'ToggleRoot': {
        const s = this.state.squads.get(cmd.squadId);
        if (!s || s.owner !== cmd.player) break;
        const rootable = unitDef(s.defId).rootable;
        if (!rootable || s.uprootRemaining > 0) break;
        if (!s.treeRooted) {
          s.treeRooted = true;
          s.moveTarget = null;
          s.path = null;
          s.pathGoal = null;
          s.command = { kind: 'defend', pointA: { ...s.pos }, auto: true };
          this.pushEvent({
            type: 'AbilityTriggered',
            id: s.id,
            abilityId: 'take-root',
            pos: { ...s.pos },
            label: 'Take Root',
          });
        } else {
          // uprooting takes a while and suppresses the economy aura afterwards
          s.uprootRemaining = rootable.uprootSeconds;
        }
        break;
      }
      case 'DeepRecall': {
        const annexDef = [...this.coreAnnexes(cmd.player)].find((u) => u.deepRecall);
        const core = this.coreAlive(cmd.player);
        if (!annexDef?.deepRecall || !core || p.annex.deepRecallCooldown > 0) break;
        const spec = annexDef.deepRecall;
        let started = 0;
        for (const w of this.state.workers.values()) {
          if (started >= spec.maxWorkers) break;
          if (w.owner !== cmd.player || w.dead) continue;
          // retreating workers only (fleeing or heading to shelter)
          if (w.state !== 'fleeing' && w.state !== 'toShelter') continue;
          w.recallChannelRemaining = spec.channelSeconds;
          started++;
        }
        if (started > 0) {
          p.annex.deepRecallCooldown = spec.cooldown;
          this.pushEvent({
            type: 'AbilityTriggered',
            id: core.id,
            abilityId: 'deep-recall',
            pos: { ...core.pos },
            label: 'Deep Recall',
          });
        }
        break;
      }
      case 'TransferWorkers': {
        const annexDef = [...this.coreAnnexes(cmd.player)].find((u) => u.workerTransfer);
        const target = this.state.buildings.get(cmd.buildingId);
        if (!annexDef?.workerTransfer || p.annex.transferCooldown > 0) break;
        if (!target || target.owner !== cmd.player || target.state !== 'active' || target.isCore) break;
        if (!buildingDef(target.defId).conduit || !this.state.connectedConduits.has(target.id)) break;
        const spec = annexDef.workerTransfer;
        const tfp = footprintOf(buildingDef(target.defId), target.rot);
        let moved = 0;
        while (moved < spec.maxWorkers && p.shelterOccupants.length > 0) {
          const id = p.shelterOccupants.shift()!;
          const w = this.state.workers.get(id);
          if (!w || w.dead) continue;
          const angle = (moved / spec.maxWorkers) * Math.PI * 2;
          const out = this.nav.nearestWalkable(
            vec(target.pos.x + Math.cos(angle) * (tfp.w / 2 + 2), target.pos.y + Math.sin(angle) * (tfp.h / 2 + 2)),
          );
          w.pos = { ...out };
          w.prevPos = { ...out };
          w.lastOpenPos = { ...out };
          w.path = null;
          w.state = 'idle';
          w.arrivalRemaining = spec.arriveSeconds; // vulnerable while materializing
          w.homeId = this.nearestWorkerHub(cmd.player, target.pos)?.id ?? w.homeId;
          w.depositId = null;
          moved++;
        }
        if (moved > 0) {
          p.annex.transferCooldown = spec.cooldown;
          this.pushEvent({
            type: 'AbilityTriggered',
            id: target.id,
            abilityId: 'worker-transfer',
            pos: { ...target.pos },
            label: 'Worker Transfer',
          });
        }
        break;
      }
      case 'SetTithe': {
        if (p.annex.apotheosisUsed || p.annex.apotheosis) break;
        const annexDef = [...this.coreAnnexes(cmd.player)].find((u) => u.bloodTithe);
        if (!annexDef?.bloodTithe) break;
        if (cmd.active && !p.annex.titheActive) p.annex.titheTimer = annexDef.bloodTithe.period;
        p.annex.titheActive = cmd.active;
        break;
      }
      case 'SetEchoArmed':
        p.annex.echoArmed = cmd.armed;
        break;
      case 'ChooseApotheosis':
        p.annex.apotheosisForm = cmd.form;
        break;
      case 'CheatResources':
        p.power += cmd.power;
        p.essence += cmd.essence;
        break;
      case 'CheatAscension':
        p.ascensionDiscovered = 4;
        p.ascensionPlayable = 4;
        this.recomputeCaps(cmd.player);
        break;
      case 'CheatCorpse': {
        const core = this.coreAlive(cmd.player);
        const at = core ? core.pos : vec(120, 80);
        for (let i = 0; i < cmd.count; i++) {
          const angle = (i / cmd.count) * Math.PI * 2;
          this.state.corpseMarks.push({
            id: this.newId(),
            pos: vec(at.x + Math.cos(angle) * 8, at.y + Math.sin(angle) * 8),
            remaining: TUNING.corpseMarkDuration,
            sourceOwner: cmd.player,
          });
        }
        break;
      }
      case 'CheatFinishRituals':
        for (const r of this.state.rituals) {
          if (r.owner === cmd.player) r.remaining = 0.05;
        }
        break;
      case 'EpicBattle':
        this.stageEpicBattle();
        break;
    }
  }

  /**
   * Developer staging scene: put both armies into an immediate central engagement and
   * seed the field with real combat deaths so the renderer shows a lived-in battlefield.
   * It is deliberately a sim command, keeping the scene deterministic and selectable.
   */
  private stageEpicBattle(): void {
    if (this.epicBattleStaged) return;
    this.epicBattleStaged = true;
    this.state.phase = 'running';
    this.state.countdownRemaining = 0;
    this.state.paused = false;

    const center = vec(this.map.width / 2, this.map.height / 2);
    const battleRosters: UnitDef[][] = this.state.players.map((player) =>
      [...defs.units.values()].filter((unit) =>
        unit.faction === player.faction &&
        !unit.roleTags.includes('worker') &&
        !unit.isBoss &&
        !unit.rootable,
      ),
    );

    for (const owner of this.playerIds()) {
      const p = this.state.players[owner];
      p.power = Math.max(p.power, 5000);
      p.essence = Math.max(p.essence, 2500);
      p.ascensionDiscovered = 4;
      p.ascensionPlayable = 4;
      const roster = battleRosters[owner];
      if (roster.length === 0) continue;

      for (let i = 0; i < 24; i++) {
        const row = i % 6;
        const column = Math.floor(i / 6);
        const core = this.map.corePositions[owner];
        const toward = core.x < this.map.width / 2 ? 1 : -1;
        const x = core.x + toward * (54 + column * 9);
        const y = core.y - 25 + row * 10;
        const squad = this.spawnSquad(roster[i % roster.length].id, owner, vec(x, y));
        this.issueSquadCommand(squad, 'attack', center);
      }

      const boss = [...defs.units.values()].find((unit) => unit.faction === p.faction && unit.isBoss);
      if (boss) {
        const core = this.map.corePositions[owner];
        const squad = this.spawnSquad(boss.id, owner, vec(core.x + (core.x < center.x ? 60 : -60), core.y));
        this.issueSquadCommand(squad, 'attack', center);
      }
    }

    // Real deaths emit the same events as combat, which the renderer turns into bodies.
    const corpseRosters = battleRosters;
    for (const owner of this.playerIds()) {
      const roster = corpseRosters[owner];
      if (roster.length === 0) continue;
      for (let i = 0; i < 8; i++) {
        const x = 92 + ((i * 13 + owner * 7) % 56);
        const y = 52 + ((i * 17 + owner * 11) % 56);
        const squad = this.spawnSquad(roster[i % roster.length].id, owner, vec(x, y));
        this.killSquad(squad);
      }
    }

    for (const owner of this.playerIds()) this.recomputeCaps(owner);
  }

  /** A worker hub fell: its workers fall back to the owner's Core zone. */
  private rehomeWorkers(hubId: EntId, owner: PlayerId): void {
    let lost = false;
    const core = this.coreAlive(owner);
    for (const w of this.state.workers.values()) {
      if (w.owner === owner && w.homeId === hubId) {
        lost = true;
        w.homeId = core ? core.id : null;
        // drop the stale assignment so the solver re-picks inside the new zone
        if (w.state === 'toDeposit' || w.state === 'gathering') {
          w.depositId = null;
          w.state = w.carrying ? 'toDropOff' : 'idle';
          w.path = null;
        }
      }
    }
    // survivors dog-piled onto the Core zone — spread them to hubs with room
    if (lost) this.rebalanceWorkers(owner);
  }

  /**
   * Spread workers across zones after a hub is gained or lost (owner request): each zone's
   * excess (ranked by ascending id, highest ids move) walks to the nearest hub with room.
   * Deterministic — Map iteration order + id tie-breaks, no RNG.
   */
  rebalanceWorkers(owner: PlayerId): void {
    const hubs = this.workerHubs(owner);
    if (hubs.length === 0) return;
    const byHub = new Map<EntId, WorkerEnt[]>();
    for (const hub of hubs) byHub.set(hub.id, []);
    const excess: WorkerEnt[] = [];
    for (const w of this.state.workers.values()) {
      if (w.owner !== owner || w.dead || w.state === 'sheltered') continue;
      const list = w.homeId !== null ? byHub.get(w.homeId) : undefined;
      if (list) list.push(w);
      else excess.push(w); // homeless stray — always rehome
    }
    const room = new Map<EntId, number>();
    for (const hub of hubs) {
      const list = byHub.get(hub.id)!; // Map-order push keeps ascending-id order per zone
      const cap = this.zoneWorkerCap(owner, hub);
      room.set(hub.id, Math.max(0, cap - list.length));
      for (let i = list.length - 1; i >= cap; i--) excess.push(list[i]); // highest ids move first
    }
    for (const w of excess) {
      let best: Building | null = null;
      let bestD = Infinity;
      for (const hub of hubs) {
        if ((room.get(hub.id) ?? 0) <= 0) continue;
        const d = dist(w.pos, hub.pos);
        if (d < bestD - 1e-9 || (best !== null && Math.abs(d - bestD) <= 1e-9 && hub.id < best.id)) {
          bestD = d;
          best = hub;
        }
      }
      // nowhere with room: strays adopt the nearest hub anyway (over-cap at half rate);
      // zone-excess workers stay put rather than shuffle between equally full zones
      if (!best) {
        if (w.homeId === null || !this.state.buildings.has(w.homeId)) {
          w.homeId = this.nearestWorkerHub(owner, w.pos)?.id ?? null;
        }
        continue;
      }
      if (best.id !== w.homeId) {
        w.homeId = best.id;
        room.set(best.id, room.get(best.id)! - 1);
        if (w.state === 'toDeposit' || w.state === 'gathering') {
          w.depositId = null;
          w.state = w.carrying ? 'toDropOff' : 'idle';
          w.path = null;
        }
      }
    }
  }

  destroyBuildingSilently(b: Building): void {
    const awakening = buildingDef(b.defId).traits?.find((trait) => trait.kind === 'finalAwakening');
    if (awakening) this.spawnSquad(awakening.unitId, b.owner, b.pos, { temporarySeconds: awakening.duration });
    this.state.buildings.delete(b.id);
    this.rehomeWorkers(b.id, b.owner);
    this.stampBuildingNav(b, -1);
    const p = this.state.players[b.owner];
    if (b.cardId) {
      const cr = p.cards[b.cardId];
      if (cr) cr.activeCopies = Math.max(0, cr.activeCopies - 1);
    }
    const ruin = this.state.ruins.find((r) => r.buildingId === b.id);
    if (ruin) ruin.buildingId = null;
  }

  /** Removes the owner's site tower when a site falls (used by neutralize). */
  removeSiteTower(siteId: string): void {
    for (const b of [...this.state.buildings.values()]) {
      if (buildingDef(b.defId).isSiteTower && b.siteId === siteId) this.destroyBuildingSilently(b);
    }
  }

  unitQueueDenial(b: Building, unitId: string): string | null {
    const d = buildingDef(b.defId);
    const entry = d.roster?.entries.find((e) => e.unitId === unitId);
    if (!entry || !defs.units.has(unitId)) return 'invalidUnit';
    const p = this.state.players[b.owner];
    if (b.queue.length >= (d.roster?.queueSize ?? 5)) return 'queueFull';
    if (p.ascensionPlayable < entry.ascension) return 'wrongAscension';
    if (p.power < entry.cost.power) return 'insufficientPower';
    if (p.essence < entry.cost.essence) return 'insufficientEssence';
    const cmd = entry.cost.command ?? 0;
    if (p.commandUsed + cmd > p.commandCap) return 'insufficientCommand';
    const ud = unitDef(unitId);
    // worker variants occupy workforce, not command
    if (ud.workerVariant && p.workforceUsed >= p.workforceCap) return 'workforceFull';
    // global active-copy cap (Banner Captain 2, Master Builder 4)
    if (ud.maxActiveUnits !== undefined) {
      let active = 0;
      for (const s of this.state.squads.values()) if (s.owner === b.owner && s.defId === unitId) active++;
      for (const w of this.state.workers.values()) if (w.owner === b.owner && w.defId === unitId) active++;
      for (const ob of this.state.buildings.values()) {
        if (ob.owner !== b.owner) continue;
        for (const q of ob.queue) if (q.unitId === unitId) active++;
      }
      if (active >= ud.maxActiveUnits) return 'activeLimit';
    }
    return null;
  }

  tryEnqueueUnit(b: Building, unitId: string): boolean {
    if (this.unitQueueDenial(b, unitId)) return false;
    const d = buildingDef(b.defId);
    const entry = d.roster!.entries.find((e) => e.unitId === unitId)!;
    const p = this.state.players[b.owner];
    const cmd = entry.cost.command ?? 0;
    p.power -= entry.cost.power;
    p.essence -= entry.cost.essence;
    b.queue.push({
      unitId,
      remaining: entry.trainTime,
      total: entry.trainTime,
      costPower: entry.cost.power,
      costEssence: entry.cost.essence,
      costCommand: cmd,
    });
    this.recomputeCaps(b.owner);
    return true;
  }

  issueSquadCommand(
    s: Squad,
    kind: import('./state').HLKind,
    targetPos?: Vec2,
    targetEntity?: EntId,
  ): void {
    // rootable guardians (Rootwarden Treants) cannot be ordered beyond friendly territory
    if (
      unitDef(s.defId).rootable &&
      targetPos &&
      (kind === 'move' || kind === 'attack' || kind === 'defend' || kind === 'patrol' || kind === 'raid') &&
      !this.inFriendlyTerritory(targetPos, s.owner)
    ) {
      return;
    }
    // any new order dismounts a garrisoned squad beside its wall
    if (s.mountedOnWall !== null) {
      dismountWallSquad(this, s);
    }
    // any new order dismounts from a host squad (§27: mounting is a stance, not a cage)
    if (s.mountedOnSquad !== null) {
      s.mountedOnSquad = null;
      s.pos = this.nav.nearestWalkable(s.pos, 12, s.owner);
      s.prevPos = { ...s.pos };
      s.lastOpenPos = { ...s.pos };
    }
    // leaving an escort
    if (s.escortOf !== null && kind !== 'escort') s.escortOf = null;
    s.targetId = null;
    s.engaged = false;
    s.path = null;
    s.pathGoal = null;
    s.moveTarget = null;
    s.repathCooldown = 0;
    s.stuckTime = 0;
    s.progressGoal = null;
    s.bestRouteRemaining = Infinity;
    s.progressNavRevision = this.nav.revision;
    s.progressRouteSignature = 0;
    s.breachResume = null;
    s.retargetTimer = 0;
    // A fresh player/AI order supersedes an automatic dash; otherwise the charge branch
    // keeps overriding the new move/attack command until its timer expires.
    s.charge = null;
    s.commandIssuedTick = this.state.tick;
    switch (kind) {
      case 'move':
        s.command = { kind: 'move', targetPos };
        break;
      case 'attack':
        if (targetEntity !== undefined) {
          const targetBuilding = this.state.buildings.get(targetEntity);
          const target = targetPosition(this, targetEntity);
          const rawAccessGoal = targetBuilding ? buildingAccessPoint(s.pos, targetBuilding) : target;
          const accessGoal = rawAccessGoal
            ? this.nav.nearestWalkable(rawAccessGoal, 12, s.owner, s.pos)
            : null;
          if (target && accessGoal && !this.nav.canReach(s.pos, accessGoal, s.owner)) {
            // Do not pin any entity behind a closed wall. Attack-move advances to the
            // reachable frontier, fights the blocker, and reacquires locally after a breach.
            s.command = { kind: 'attack', targetPos: accessGoal };
            break;
          }
        }
        s.command = { kind: 'attack', targetPos, targetEntity };
        break;
      case 'capture': {
        // snap to nearest site to the click
        let best: string | null = null;
        let bestD = Infinity;
        for (const site of this.state.sites) {
          const dd = targetPos ? dist(site.pos, targetPos) : Infinity;
          if (dd < bestD) {
            bestD = dd;
            best = site.id;
          }
        }
        if (best !== null && bestD < 40) {
          const site = this.state.sites.find((x) => x.id === best)!;
          s.command = { kind: 'capture', siteId: best, targetPos: { ...site.pos } };
        }
        break;
      }
      case 'defend': {
        const center = targetPos ?? { ...s.pos };
        // snap to site if close
        let siteId: string | undefined;
        for (const site of this.state.sites) {
          if (dist(site.pos, center) <= TUNING.captureRadius + 4) siteId = site.id;
        }
        s.command = { kind: 'defend', pointA: { ...center }, siteId };
        break;
      }
      case 'patrol':
        if (targetPos) {
          s.command = { kind: 'patrol', pointA: { ...s.pos }, pointB: { ...targetPos }, patrolLegToB: true };
        }
        break;
      case 'raid':
        s.command = { kind: 'raid', targetPos: targetPos ?? { ...s.pos } };
        break;
      case 'escort': {
        const boss = targetEntity !== undefined ? this.state.squads.get(targetEntity) : undefined;
        if (boss && boss.isBoss && boss.owner === s.owner && boss.id !== s.id) {
          s.escortOf = boss.id;
          s.command = { kind: 'escort', targetEntity: boss.id };
        }
        break;
      }
      case 'withdraw': {
        s.command = { kind: 'withdraw' };
        break;
      }
      case 'idle':
        s.command = { kind: 'idle', pointA: { ...s.pos } };
        break;
    }
  }

  // ---------------------------------------------------------------- tick

  tick(): void {
    const st = this.state;
    if (st.paused) return;
    // Events are per-tick: consumers read state.events after every tick() call, so the
    // early-return phases must clear last tick's list or its FX/audio replay every frame
    // (the winning core explosion used to shake the camera forever).
    st.events = [];
    if (st.phase === 'countdown') {
      st.countdownRemaining -= TUNING.dt;
      if (st.countdownRemaining <= 0) st.phase = 'running';
      // camera-only phase: still drain commands so allocation toggles are not lost
      this.drainCommands();
      return;
    }
    if (st.phase === 'ended') return;

    st.tick++;
    st.time += TUNING.dt;

    // snapshot prev positions for interpolation
    for (const s of st.squads.values()) {
      s.prevPos.x = s.pos.x;
      s.prevPos.y = s.pos.y;
    }
    for (const w of st.workers.values()) {
      w.prevPos.x = w.pos.x;
      w.prevPos.y = w.pos.y;
    }
    for (const pr of st.projectiles.values()) {
      pr.prevPos.x = pr.pos.x;
      pr.prevPos.y = pr.pos.y;
    }

    this.drainCommands(); //            1. commands
    stepNetwork(this); //               1b. conduit network connectivity
    stepSpellsAndZones(this); //        2. spells/zones
    stepCards(this); //                 3. card cooldowns
    stepRituals(this); //               3b. ritual channels
    stepConstruction(this); //          4a. construction
    stepGates(this); //                 4b. castle auto-gates
    stepProduction(this); //            4c. production
    stepBuildingTraits(this); //        4c2. non-Core building identities
    stepAnnexes(this); //               4d. Core Annex System (spawners, tithe, echoes)
    stepWorkers(this); //               5. workers
    stepCommands(this); //              6. high-level commands
    stepMovement(this); //              7. movement
    stepMounting(this); //              7b. slave rider positions to hosts
    stepAbilities(this); //             8. abilities
    stepCombat(this); //                9. combat + projectiles
    stepTraps(this); //                 9b. traps
    stepCoreDefense(this); //           9c. faction core defenses
    stepSites(this); //                 10. sites
    // 11. derived caps — recomputed on events; refresh ascension playable here
    for (const pid of this.playerIds()) {
      const p = st.players[pid];
      const playable = (Math.min(3, this.sitesOwned(pid)) + 1) as 1 | 2 | 3 | 4;
      p.ascensionPlayable = playable;
    }
    stepRecovery(this); //              12. recovery & repair-over-time
    stepCorpse(this); //                12b. corpse-mark decay
    // 13. victory check happens in destroyBuilding
  }
}
